import {useMutation} from '@tanstack/react-query';
import {getLocalStorage} from '@/Utils/CommonUtils';
import ChessRestClient from '@/Api/ChessRestClient';
import useChatRestClient from '@/Api/ChatRestClient';

interface CaissaChatbotResponse {
  response: string;
  // Present only for psychology-mode answers -- the cc_caissa_chat_log row id
  // this answer was logged under, used to attach a reaction later.
  chat_log_id?: number | null;
}

export default function useGetCaissaChatbotResponse(
  handleOnSuccess?: (data: CaissaChatbotResponse) => void,
) {
  const ChatRestClient = useChatRestClient();
  const mutation = useMutation<
    CaissaChatbotResponse,
    Error,
    {user_message: string; is_psychology_mode?: boolean}
  >(
    async ({user_message, is_psychology_mode}) => {
      const response = await ChatRestClient.post(
        '/gpt/v1/get_caissa_chatbot_response',
        {
          user_message,
          is_psychology_mode,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );
      return response.data;
    },
    {
      onSuccess: data => {
        if (handleOnSuccess) {
          handleOnSuccess(data);
        }
      },
    },
  );

  return {
    ...mutation,
  };
}

// Reaction is stored/sent as an integer code (matches the backend + DB):
//   1 = thumbs_up (👍)
//   2 = heart      (❤️)
//   3 = confused   (😕)
//   4 = thumbs_down(👎)
export type CaissaReaction = 1 | 2 | 3 | 4;

// Records (or clears, when reaction is null) the user's reaction to a specific
// Caissa answer. Fire-and-forget from the UI's perspective -- the local
// optimistic state is the source of truth for what the user sees.
export function useSubmitCaissaReaction() {
  const ChatRestClient = useChatRestClient();
  return useMutation<
    {success: boolean; chat_log_id: number; reaction: CaissaReaction | null},
    Error,
    {chat_log_id: number; reaction: CaissaReaction | null}
  >(async ({chat_log_id, reaction}) => {
    const response = await ChatRestClient.post(
      '/gpt/v1/submit_caissa_reaction',
      {chat_log_id, reaction},
      {headers: {'Content-Type': 'application/json'}},
    );
    return response.data;
  });
}
